import { useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import DefaultStatisticsCard from "./components/RecvCard";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCustomerValue } from "providers/CustomerContext";
import { ListLayout } from "ui-components/Layout";
import { TableCard } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import MDButton from "components/MDButton";
import { SelectFields } from "ui-components/Form";
import { getRecvDashboardStatusCountByCustomer } from "services/data.dashboard.service";
import { LoadingDialog } from "ui-components/Alert";
import { getRecvDashboardStatusCountByCustomer2 } from "services/data.dashboard.service";

function RecvMainDashboard() {
  const { customerOpt } = useCustomerValue();

  const enabled = Boolean(customerOpt.length);

  let currentDate = new Date();
  const [dateEnd, setDateEnd] = useState(new Date(currentDate));
  const [dateStart, setDateStart] = useState(
    new Date(currentDate.setDate(currentDate.getDate() - 7))
  );

  const [tabs, setTabs] = useState([
    { label: "Open", count: 0 },
    { label: "GRN", count: 0 },
    { label: "PutawayGenerated", count: 0 },
  ]);

  const [tableData, setTableData] = useState([]);
  const [filterMethod, setFilterMethod] = useState("All Type");

  const { isFetching, refetch } = useQuery({
    queryKey: ["recv-status-by-customers", [filterMethod, customerOpt]],
    enabled: enabled,
    queryFn: () =>
      // getRecvDashboardStatusCountByCustomer({
      getRecvDashboardStatusCountByCustomer2({
        orderType: filterMethod,
        dateStart: Boolean(dateStart) ? dayjs(dateStart).format("YYYY-MM-DD") : null,
        dateEnd: Boolean(dateEnd) ? dayjs(dateEnd).format("YYYY-MM-DD") : null,
        customers: customerOpt,
      }),
    onSuccess: (data) => {
      const result = data.data.data;

      const openCount = result.reduce((sum, customer) => {
        const openStatus = customer.status.find(status => status.label === "Open");
        return sum + (openStatus ? openStatus.count : 0);
      }, 0);

      const grnCount = result.reduce((sum, customer) => {
        const grnStatus = customer.status.find(status => status.label === "GRN");
        return sum + (grnStatus ? grnStatus.count : 0);
      }, 0);

      const putawayCount = result.reduce((sum, customer) => {
        const putawayStatus = customer.status.find(status => status.label === "PutawayGenerated");
        return sum + (putawayStatus ? putawayStatus.count : 0);
      }, 0);
      
      setTabs([
        { label: "Open", count: openCount },
        { label: "GRN", count: grnCount },
        { label: "PutawayGenerated", count: putawayCount },
      ])
      console.log(result)

      const flattenedData = result.map((obj) => {
        const flattened = { customerCode: obj.customerCode };
        var total = 0;
        obj.status.forEach((s) => {
          flattened[s.label] = s.count;
          total += s.count;
        });
        flattened["totalQuantity"] = obj.totalQuantity;
        flattened["total"] = total;
        return flattened;
      });

      setTableData(flattenedData);
    },
  });

  const columns = [
    {
      header: "Customer Code",
      accessorKey: "customerCode",
      footer: "Total",
    },
    {
      header: "Open",
      accessorFn: (row) => (row["Open"] ?? 0).toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Open").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Goods Received Note",
      accessorFn: (row) => (row["GRN"] ?? 0).toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Goods Received Note").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Putaway Generated",
      accessorFn: (row) => (row["PutawayGenerated"] ?? 0).toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Putaway Generated").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Total",
      accessorFn: (row) => (row["total"]??0).toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Total").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Total Quantity",
      accessorFn: (row) => row["totalQuantity"].toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Total Quantity").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "% Completed",
      accessorKey: "percCompleted",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const {
          "PutawayGenerated": putawayGenerated,
          total,
        } = row.original;
        const complete = total !== 0 ? putawayGenerated / total : 0;
        let bgcolor = "rgba(255,255,255,0)"; //white
        if (complete > 0 && complete <= 0.5) {
          bgcolor = "rgba(255,0,0,0.5)"; //red
        } else if (complete > 0.5 && complete <= 0.8) {
          bgcolor = "rgba(255,165,0,0.5)"; //orang
        } else if (complete > 0.8 && complete <= 0.9) {
          bgcolor = "rgba(255,255,0,0.5)"; //yellow
        } else if (complete > 0.9) {
          bgcolor = "rgba(0,255,0,0.5)"; //green
        } else {
          bgcolor = "white";
        }
        const completeInPerc = complete * 100;
        const rounded = Math.round(completeInPerc * 10) / 10;
        return (
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <div
              style={{
                backgroundColor: bgcolor,
                padding: "8px",
                textAlign: "center",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {rounded}%
            </div>
          </div>
        );
      },
      footer: ({ table }) => {
        const filteredRows = table.getFilteredRowModel().rows;
        const totalCompleted = filteredRows.reduce(
          (total, row) => total + parseInt(row.getValue("Putaway Generated").replace(/[^0-9.-]+/g, "")),
          0
        );
        const totalOverall = filteredRows.reduce(
          (total, row) => total + parseInt(row.getValue("Total").replace(/[^0-9.-]+/g, "")),
          0
        );
        const complete =
          totalOverall !== 0 ? totalCompleted / totalOverall : 0;
        let bgcolor = "rgba(255,255,255,0)"; //white
        if (complete > 0 && complete <= 0.5) {
          bgcolor = "rgba(255,0,0,0.5)"; //red
        } else if (complete > 0.5 && complete <= 0.8) {
          bgcolor = "rgba(255,165,0,0.5)"; //orang
        } else if (complete > 0.8 && complete <= 0.9) {
          bgcolor = "rgba(255,255,0,0.5)"; //yellow
        } else if (complete > 0.9) {
          bgcolor = "rgba(0,255,0,0.5)"; //green
        } else {
          bgcolor = "white";
        }
        const completedPercentage = complete * 100;
        const roundedPercentage = Math.round(completedPercentage * 10) / 10;
        return (
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <div
              style={{
                backgroundColor: bgcolor,
                padding: "8px",
                textAlign: "center",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {roundedPercentage}%
            </div>
          </div>
        );
      },
    },
  ];

  const [tableColumn, setTableColumn] = useState(columns);

  // useEffect(() => {
  //   if (filterMethod === "Misc"){
  //     setTableColumn(columns.filter(x => x.header !== "Open"));
  //   } else {
  //     setTableColumn(columns);
  //   }
  // }, [filterMethod])

  const customProps = {
    startDate: dateStart,
    setStartDate: setDateStart,
    endDate: dateEnd,
    setEndDate: setDateEnd,
  };

  return (
    <ListLayout lg={12}>
      {(isFetching)&&<LoadingDialog />}
      <MDBox py={3}>
        <Grid container spacing={3}>
          {/* {filterMethod !== "Misc" && ( */}
            <Grid item xs={4}>
            <DefaultStatisticsCard
              title={filterMethod === "All Type" ? "Open PO / CO" : "Open"}
              count={tabs.find((x) => x.label === "Open").count}
              color={"#bdf9f9"}
            />
          </Grid>
          {/* )} */}
          <Grid item xs={4}>
            <DefaultStatisticsCard
              title="Goods Received Note"
              count={tabs.find((x) => x.label === "GRN").count}
              color={"#d9d9ff"}
            />
          </Grid>
          <Grid item xs={4}>
            <DefaultStatisticsCard
              title="Putaway Generated"
              count={tabs.find((x) => x.label === "PutawayGenerated").count}
              color={"lightyellow"}
            />
          </Grid>
        </Grid>
      </MDBox>

      <TableCard>
      <Grid container justifyContent="space-between" alignItems="flex-start" margin="10px">
          <Grid item xs={3}>
                <SelectFields
                  field={{ name: "", label: "Filter by Date", value: filterMethod, errors: {} }}
                  options={["All Type", "PO", "CO", "Misc"]}
                  onChange={(e, v) => setFilterMethod(v)}
                />
              </Grid>
          <Grid item xs={9}>
            <Grid justifyContent="flex-end" container spacing={3} sx={{ width: "100%" }}>
              <Grid item xs={6}>
                <CustomDateRangePicker {...customProps} />
              </Grid>
              <Grid item>
                <MDButton
                  color="info"
                  disabled={!dateStart || !dateEnd}
                  onClick={() => {
                    refetch();
                  }}
                >
                  search
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <BasicTable columns={tableColumn} data={tableData} />
      </TableCard>
    </ListLayout>
  );
}

export default RecvMainDashboard;
